import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Button from '../components/common/Button';
import Modal from '../components/common/Modal';
import AbaProtegida from '../components/common/AbaProtegida';
import FiliaisService, { Filial } from '../services/filiaisService';

const Filiais: React.FC = () => {
  const { user } = useAuth();
  const [filiais, setFiliais] = useState<Filial[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalAberto, setModalAberto] = useState(false);
  const [editando, setEditando] = useState<Filial | null>(null);
  const [modalDeletarAberto, setModalDeletarAberto] = useState(false);
  const [filialParaDeletar, setFilialParaDeletar] = useState<string | null>(null);
  
  // Form state
  const [nome, setNome] = useState('');
  const [cidade, setCidade] = useState('');
  const [estado, setEstado] = useState('');
  const [endereco, setEndereco] = useState('');
  const [telefone, setTelefone] = useState('');
  const [status, setStatus] = useState<'ativo' | 'inativo'>('ativo');

  const carregarFiliais = async () => {
    setLoading(true);
    try {
      const data = await FiliaisService.buscarTodas();
      setFiliais(data);
    } catch (error) {
      console.error('Erro ao carregar filiais:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarFiliais();
  }, []);

  const abrirModal = (filial?: Filial) => {
    if (filial) {
      setEditando(filial);
      setNome(filial.nome);
      setCidade(filial.cidade);
      setEstado(filial.estado);
      setEndereco(filial.endereco || '');
      setTelefone(filial.telefone || '');
      setStatus(filial.status);
    } else {
      setEditando(null);
      setNome('');
      setCidade('');
      setEstado('');
      setEndereco('');
      setTelefone('');
      setStatus('ativo');
    }
    setModalAberto(true);
  };

  const fecharModal = () => {
    setModalAberto(false);
    setEditando(null);
  };

  const salvar = async () => {
    try {
      const filialData: Filial = {
        nome,
        cidade,
        estado,
        endereco,
        telefone,
        status
      };

      if (editando) {
        await FiliaisService.atualizar(editando.id!, filialData);
      } else {
        await FiliaisService.criar(filialData);
      }

      fecharModal();
      carregarFiliais();
    } catch (error) {
      console.error('Erro ao salvar filial:', error);
      alert('Erro ao salvar filial');
    }
  };

  const deletar = async (id: string) => {
    setFilialParaDeletar(id);
    setModalDeletarAberto(true);
  };

  const confirmarDeletar = async () => {
    if (!filialParaDeletar) return;

    try {
      await FiliaisService.deletar(filialParaDeletar);
      setModalDeletarAberto(false);
      setFilialParaDeletar(null);
      carregarFiliais();
    } catch (error) {
      console.error('Erro ao deletar filial:', error);
      alert('Erro ao deletar filial');
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Gestão de Filiais</h1>
        <Button onClick={() => abrirModal()}>
          + Nova Filial
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Nome
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Cidade
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filiais.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                    Nenhuma filial cadastrada
                  </td>
                </tr>
              ) : (
                filiais.map((filial) => (
                  <tr key={filial.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {filial.nome}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {filial.cidade}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {filial.estado}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        filial.status === 'ativo' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {filial.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => abrirModal(filial)}
                        className="text-blue-600 hover:text-blue-900 mr-4"
                      >
                        Editar
                      </button>
                      <button
                        onClick={() => deletar(filial.id!)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Deletar
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        isOpen={modalAberto}
        onClose={fecharModal}
        title={editando ? 'Editar Filial' : 'Nova Filial'}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nome *
            </label>
            <input
              type="text"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Ex: Filial Rio Largo"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Cidade *
            </label>
            <input
              type="text"
              value={cidade}
              onChange={(e) => setCidade(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Ex: Rio Largo"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Estado *
            </label>
            <input
              type="text"
              value={estado}
              onChange={(e) => setEstado(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Ex: AL"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Endereço
            </label>
            <input
              type="text"
              value={endereco}
              onChange={(e) => setEndereco(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Ex: Rua Principal, 123"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Telefone
            </label>
            <input
              type="text"
              value={telefone}
              onChange={(e) => setTelefone(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Ex: (82) 99999-9999"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status *
            </label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as 'ativo' | 'inativo')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="ativo">Ativo</option>
              <option value="inativo">Inativo</option>
            </select>
          </div>
          <Button onClick={salvar} className="w-full">
            {editando ? 'Atualizar' : 'Criar'} Filial
          </Button>
        </div>
      </Modal>

      <Modal
        isOpen={modalDeletarAberto}
        onClose={() => {
          setModalDeletarAberto(false);
          setFilialParaDeletar(null);
        }}
        title="Confirmar Deleção"
      >
        <div className="space-y-4">
          <p className="text-gray-700">
            Tem certeza que deseja deletar esta filial? Esta ação não pode ser desfeita.
          </p>
          <div className="flex gap-3">
            <Button
              onClick={() => {
                setModalDeletarAberto(false);
                setFilialParaDeletar(null);
              }}
              className="flex-1 bg-gray-500 hover:bg-gray-600"
            >
              Cancelar
            </Button>
            <Button onClick={confirmarDeletar} className="flex-1 bg-red-500 hover:bg-red-600">
              Deletar
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

const FiliaisProtegido = () => (
  <AbaProtegida nomeAba="Filiais">
    <Filiais />
  </AbaProtegida>
);

export default FiliaisProtegido;
